import { useState, useRef, useEffect, useCallback } from 'react';

export interface UseSmoothStreamOptions {
  charDelay?: number; // ms per character
  mode?: 'char' | 'word';
}

interface SmoothStreamApi {
  stream: string;               // Animated visible text
  addPart: (part: string) => void; // Add raw chunk from network
  reset: () => void;            // Reset buffer + animation
  isAnimating: boolean;         // Whether animation still in progress
  setSpeed: (ms: number) => void;
}

export function useSmoothStream({ charDelay = 8, mode = 'char' }: UseSmoothStreamOptions = {}): SmoothStreamApi {
  const [parts, setParts] = useState<string[]>([]);
  const [stream, setStream] = useState('');
  const [speed, setSpeed] = useState(charDelay);

  const frameRef = useRef<number | null>(null);
  const lastTimeRef = useRef(0);
  const indexRef = useRef(0);
  const fullRef = useRef('');
  const animatingRef = useRef(false);

  const addPart = useCallback((p: string) => {
    if (!p) return;
    setParts(prev => {
      const next = [...prev, p];
      fullRef.current = next.join('');
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    lastTimeRef.current = 0;
    indexRef.current = 0;
    fullRef.current = '';
    setParts([]);
    setStream('');
    animatingRef.current = false;
  }, []);

  // Animation loop
  useEffect(() => {
    if (frameRef.current) return; // already animating
    if (animatingRef.current) return;
    const animate = (t: number) => {
      const full = fullRef.current;
      if (indexRef.current >= full.length) {
        animatingRef.current = false;
        frameRef.current = null;
        return;
      }
      if (t - lastTimeRef.current >= speed) {
        if (mode === 'word') {
          const nextSpace = full.indexOf(' ', indexRef.current + 1);
          indexRef.current = nextSpace === -1 ? full.length : nextSpace + 1;
        } else {
          indexRef.current += 1;
        }
        setStream(full.slice(0, indexRef.current));
        lastTimeRef.current = t;
      }
      frameRef.current = requestAnimationFrame(animate);
    };
    if (fullRef.current.length > 0 && indexRef.current < fullRef.current.length) {
      animatingRef.current = true;
      frameRef.current = requestAnimationFrame(animate);
    }
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      animatingRef.current = false;
    };
  }, [parts, mode, speed]);

  return {
    stream,
    addPart,
    reset,
    isAnimating: indexRef.current < fullRef.current.length,
    setSpeed: (ms: number) => setSpeed(ms < 1 ? 1 : ms)
  };
}
